import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ReportProblemModal } from '../../components/ReportProblemModal';
export function CleaningInProgressPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  // Timer state (starts at 45 minutes 32 seconds for demo)
  const [seconds, setSeconds] = useState(2732); // 00:45:32
  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  // Active report state - shows after reporting
  const [hasActiveReport, setHasActiveReport] = useState(false);
  const [reportExpanded, setReportExpanded] = useState(false);
  // Separate products from other preferences
  const productsData = {
    preferred: {
      id: 'preferred',
      titleKey: 'preferredProducts',
      emoji: '✅',
      items: ['Detergente Bio', 'Anticalcare X', 'Sgrassatore Limone']
    },
    avoid: {
      id: 'avoid',
      titleKey: 'avoidProducts',
      emoji: '❌',
      items: ['Candeggina', 'Ammoniaca']
    }
  };
  // Special preferences and delicate materials
  const specialAndDelicate = {
    special: {
      id: 'special',
      titleKey: 'specialPreferences',
      emoji: '⭐',
      items: ['Lenzuola sempre stirate', 'Cuscini divano sistemati in modo specifico', 'Lasciare luci ingresso accese']
    },
    delicate: {
      id: 'delicate',
      titleKey: 'delicateMaterials',
      emoji: '⚠️',
      items: ['Parquet antico', 'Marmo Carrara', 'Tessuti delicati']
    }
  };
  // Notes
  const notes = {
    id: 'notes',
    titleKey: 'additionalNotes',
    emoji: '📝',
    text: 'Il parquet in salotto è molto delicato, usare solo prodotti specifici e panno ben strizzato.'
  };
  // Timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor(totalSeconds % 3600 / 60);
    const secs = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };
  const handleFinishCleaning = () => {
    navigate('/fornitore/after-photos');
  };
  const handleReportProblem = (data: any) => {
    console.log('Problem reported:', data);
    setReportModalOpen(false);
    setHasActiveReport(true);
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-698">
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-32" data-id="element-699">
        {/* Page Header with Timer */}
        <div className="mb-12 flex items-start justify-between" data-id="element-700">
          <div data-id="element-701">
            <h1 className="text-4xl font-bold text-navy mb-3" data-id="element-702">
              {t.fornitore.cleaningInProgress.title}
            </h1>
            <p className="text-base text-gray-600 font-medium" data-id="element-703">
              Via Roma 123, Appartamento 4B
            </p>
          </div>

          {/* Timer - top right */}
          <div className="text-right" data-id="element-704">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-705">
              {t.fornitore.cleaningInProgress.timerLabel}
            </p>
            <div className="text-3xl font-bold text-navy font-mono" data-id="element-706">
              {formatTime(seconds)}
            </div>
          </div>
        </div>

        {/* Host Preferences Section */}
        <div className="mb-12" data-id="element-707">
          <h2 className="text-xl font-bold text-navy mb-6" data-id="element-708">
            {t.fornitore.cleaningInProgress.hostPreferences}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-709">
            {/* Prodotti Preferiti - Top Left */}
            <Card className="p-8" data-id="element-710">
              <div className="flex items-center gap-3 mb-5" data-id="element-711">
                <span className="text-3xl" data-id="element-712">{productsData.preferred.emoji}</span>
                <span className="text-lg font-semibold text-navy" data-id="element-713">
                  {t.fornitore.cleaningInProgress.preferredProducts}
                </span>
              </div>
              <div className="flex flex-wrap gap-3" data-id="element-714">
                {productsData.preferred.items.map((item, index) => <span key={index} className="inline-block px-4 py-2 bg-green-50 text-green-700 rounded-full text-base font-medium border border-green-200" data-id="element-715">
                    {item}
                  </span>)}
              </div>
            </Card>

            {/* Prodotti da Evitare - Top Right */}
            <Card className="p-8" data-id="element-716">
              <div className="flex items-center gap-3 mb-5" data-id="element-717">
                <span className="text-3xl" data-id="element-718">{productsData.avoid.emoji}</span>
                <span className="text-lg font-semibold text-navy" data-id="element-719">
                  {t.fornitore.cleaningInProgress.avoidProducts}
                </span>
              </div>
              <div className="flex flex-wrap gap-3" data-id="element-720">
                {productsData.avoid.items.map((item, index) => <span key={index} className="inline-block px-4 py-2 bg-red-50 text-red-700 rounded-full text-base font-medium border border-red-200" data-id="element-721">
                    {item}
                  </span>)}
              </div>
            </Card>

            {/* Materiali Delicati - Bottom Left */}
            <Card className="p-8" data-id="element-722">
              <div className="flex items-center gap-3 mb-5" data-id="element-723">
                <span className="text-3xl" data-id="element-724">
                  {specialAndDelicate.delicate.emoji}
                </span>
                <span className="text-lg font-semibold text-navy" data-id="element-725">
                  {t.fornitore.cleaningInProgress.delicateMaterials}
                </span>
              </div>
              <div className="flex flex-wrap gap-3" data-id="element-726">
                {specialAndDelicate.delicate.items.map((item, index) => <span key={index} className="inline-block px-4 py-2 bg-yellow-50 text-yellow-700 rounded-full text-base font-medium border border-yellow-200" data-id="element-727">
                    {item}
                  </span>)}
              </div>
            </Card>

            {/* Preferenze Speciali - Bottom Right */}
            <Card className="p-8" data-id="element-728">
              <div className="flex items-center gap-3 mb-5" data-id="element-729">
                <span className="text-3xl" data-id="element-730">
                  {specialAndDelicate.special.emoji}
                </span>
                <span className="text-lg font-semibold text-navy" data-id="element-731">
                  {t.fornitore.cleaningInProgress.specialPreferences}
                </span>
              </div>
              <ul className="space-y-3" data-id="element-732">
                {specialAndDelicate.special.items.map((item, index) => <li key={index} className="flex items-start gap-2 text-base text-gray-700 leading-relaxed" data-id="element-733">
                    <span className="text-teal-deep mt-1" data-id="element-734">•</span>
                    <span data-id="element-735">{item}</span>
                  </li>)}
              </ul>
            </Card>

            {/* Note Aggiuntive - Full Width Bottom */}
            <Card className="p-8 md:col-span-2" data-id="element-736">
              <div className="flex items-start gap-4" data-id="element-737">
                <span className="text-3xl" data-id="element-738">{notes.emoji}</span>
                <div className="flex-1" data-id="element-739">
                  <span className="text-lg font-semibold text-navy block mb-3" data-id="element-740">
                    {t.fornitore.cleaningInProgress.additionalNotes}
                  </span>
                  <p className="text-base text-gray-700 leading-relaxed" data-id="element-741">
                    {notes.text}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Report Status Card - Only if hasActiveReport is true, foldable */}
        {hasActiveReport && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-12" data-id="element-742">
            <Card className="overflow-hidden border-red-500" data-id="element-743">
              <button onClick={() => setReportExpanded(!reportExpanded)} className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-744">
                <div className="flex items-center gap-4" data-id="element-745">
                  <span className="text-3xl" data-id="element-746">🔧</span>
                  <div className="text-left" data-id="element-747">
                    <span className="text-lg font-semibold text-navy block" data-id="element-748">
                      Segnalazione in corso
                    </span>
                    <span className="text-base text-gray-500" data-id="element-749">
                      Oggetto Rotto • In lavorazione
                    </span>
                  </div>
                </div>
                <motion.div animate={{
              rotate: reportExpanded ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-750">
                  <ChevronDown className="w-6 h-6 text-gray-400" data-id="element-751" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-752">
                {reportExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-753">
                    <div className="px-6 pb-6 pt-2 border-t border-gray-100" data-id="element-754">
                      <div className="space-y-4" data-id="element-755">
                        <div className="flex items-center gap-3 text-base" data-id="element-756">
                          <span className="text-red-600" data-id="element-757">⏱️</span>
                          <span className="text-gray-700" data-id="element-758">
                            Risoluzione prevista: ~45 minuti
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-base" data-id="element-759">
                          <span className="text-gray-500" data-id="element-760">Aperta:</span>
                          <span className="text-gray-700" data-id="element-761">10:32</span>
                        </div>
                        <div className="flex items-center gap-3 text-base" data-id="element-762">
                          <span className="text-gray-500" data-id="element-763">
                            Presa in carico:
                          </span>
                          <span className="text-gray-700" data-id="element-764">10:45</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </Card>
          </motion.div>}
      </main>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:p-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40" data-id="element-765">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4" data-id="element-766">
          <button onClick={() => setReportModalOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-lg text-red-600 hover:bg-red-50 transition-colors border-2 border-red-500" data-id="element-767">
            <AlertCircle className="w-6 h-6 text-red-600" data-id="element-768" />
            <span className="hidden md:inline text-red-600" data-id="element-769">
              {t.fornitore.cleaningInProgress.reportProblem}
            </span>
            <span className="md:hidden text-red-600" data-id="element-770">Segnala</span>
          </button>

          <Button onClick={handleFinishCleaning} className="flex items-center gap-2 text-lg" data-id="element-771">
            <CheckCircle className="w-5 h-5" data-id="element-772" />
            <span data-id="element-773">{t.fornitore.cleaningInProgress.finishCleaning}</span>
          </Button>
        </div>
      </div>

      {/* Report Problem Modal */}
      <ReportProblemModal isOpen={reportModalOpen} onClose={() => setReportModalOpen(false)} onSubmit={handleReportProblem} data-id="element-774" />
    </div>;
}